import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { MemberRole, OrganisationInviteSummary, SupportedLocale } from '@accestra/types';
import { useAuth } from '../hooks/useAuth';
import { formatDateTime } from '../i18n/formatters';
import { api, ApiError, friendlyError } from '../lib/api-client';
import { usePageMeta } from '../lib/page-meta';
import AuthAside from '../components/layout/AuthAside';
import Button from '../components/ui/Button';

interface InviteDetailResponse {
  invite: OrganisationInviteSummary;
  organisation: {
    id: string;
    slug: string;
    name: string;
  };
}

interface InviteAcceptanceResponse {
  user: {
    id: string;
    email: string;
    fullName: string;
    preferredLocale: SupportedLocale | null;
    isEmailVerified: boolean;
  };
  organisation: {
    id: string;
    slug: string;
    name: string;
    defaultLocale: SupportedLocale | null;
    isDemoWorkspace: boolean;
  };
  role: MemberRole;
  tokens: {
    accessToken: string;
    refreshToken: string;
    accessExpiresAt: number;
  };
}

interface InviteVerificationRequiredResponse {
  status: 'verification_required';
  user: {
    id: string;
    email: string;
    fullName: string;
    preferredLocale: SupportedLocale | null;
    isEmailVerified: boolean;
  };
  organisation: {
    id: string;
    slug: string;
    name: string;
    defaultLocale: SupportedLocale | null;
    isDemoWorkspace: boolean;
  };
  role: MemberRole;
  verificationEmail: {
    deliveryStatus: 'sent' | 'skipped' | 'failed';
    deliveryReason: string | null;
    expiresAt: string | null;
  };
}

const C = {
  page: 'var(--acc-color-page)',
  text: 'var(--acc-color-text)',
  muted: 'var(--acc-color-text-muted)',
  border: 'var(--acc-color-border)',
  brand: 'var(--acc-color-primary)',
  successBg: 'var(--acc-color-success-soft)',
  successBorder: 'rgba(47, 143, 91, 0.22)',
  successText: 'var(--acc-color-success)',
  dangerBg: 'var(--acc-color-danger-soft)',
  dangerBorder: 'rgba(214, 69, 69, 0.22)',
  dangerText: 'var(--acc-color-danger)',
  warningBg: 'var(--acc-color-warning-soft)',
  warningBorder: 'rgba(194, 138, 46, 0.22)',
  warningText: 'var(--acc-color-warning)',
};

function inviteStatusTone(status: OrganisationInviteSummary['status']) {
  switch (status) {
    case 'accepted':
      return { background: C.successBg, border: C.successBorder, color: C.successText };
    case 'expired':
    case 'revoked':
      return { background: C.warningBg, border: C.warningBorder, color: C.warningText };
    case 'pending':
    default:
      return { background: 'var(--acc-color-primary-soft)', border: 'rgba(15, 39, 71, 0.16)', color: C.brand };
  }
}

export default function AcceptInvitePage() {
  const { t } = useTranslation(['auth', 'common']);
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, establishSession, setPendingVerification, isLoading: authLoading } = useAuth();

  usePageMeta({
    title: t('acceptInvite.metaTitle'),
    description: t('acceptInvite.metaDescription'),
    canonicalPath: '/accept-invite',
    robots: 'noindex,nofollow',
  });

  const [detail, setDetail] = useState<InviteDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [inlineFeedback, setInlineFeedback] = useState<string | null>(null);
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);

  useEffect(() => {
    if (!token) {
      setPageError(t('acceptInvite.missingToken'));
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setPageError(null);
      try {
        const data = await api.get<InviteDetailResponse>(`/orgs/invites/${token}`, { skipAuth: true });
        if (!cancelled) {
          setDetail(data);
        }
      } catch (err) {
        if (!cancelled) {
          setPageError(friendlyError(err, t('acceptInvite.errors.load')));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [t, token]);

  const tone = detail ? inviteStatusTone(detail.invite.status) : null;
  const requiresExistingAccount = detail?.invite.recipientHasAccount === true;
  const emailMatchesInvite = detail && user ? user.email.toLowerCase() === detail.invite.email.toLowerCase() : false;

  useEffect(() => {
    setShowRegistrationForm(false);
  }, [detail?.invite.recipientHasAccount]);

  const existingUserLoginPath = useMemo(() => {
    if (!detail) return '/login';
    const params = new URLSearchParams({
      inviteToken: token ?? '',
      email: detail.invite.email,
    });
    return `/login?${params.toString()}`;
  }, [detail, token]);

  const handleAccept = async () => {
    if (!token) return;

    setAccepting(true);
    setInlineFeedback(null);
    try {
      const data = await api.post<InviteAcceptanceResponse>(`/orgs/invites/${token}/accept`);
      establishSession(data);
      navigate('/dashboard');
    } catch (err) {
      setInlineFeedback(err instanceof ApiError ? err.message : t('acceptInvite.errors.accept'));
    } finally {
      setAccepting(false);
    }
  };

  const handleRegister = async (event: FormEvent) => {
    event.preventDefault();
    if (!token) return;

    setRegistering(true);
    setInlineFeedback(null);
    try {
      const data = await api.post<InviteAcceptanceResponse | InviteVerificationRequiredResponse>(
        `/orgs/invites/${token}/register`,
        {
          fullName: fullName.trim(),
          password,
        },
        { skipAuth: true },
      );
      if ('status' in data && data.status === 'verification_required') {
        setPendingVerification({
          user: data.user,
          organisation: data.organisation,
          role: data.role,
          nextPath: '/dashboard',
          verificationEmail: data.verificationEmail,
        });
        navigate('/verify-email');
        return;
      }

      const acceptedData = data as InviteAcceptanceResponse;
      establishSession(acceptedData);
      navigate('/dashboard');
    } catch (err) {
      setInlineFeedback(err instanceof ApiError ? err.message : t('acceptInvite.errors.accept'));
    } finally {
      setRegistering(false);
    }
  };

  return (
    <div className="acc-auth-page">
      <div className="acc-auth-shell">
        <AuthAside />

        <div className="acc-auth-card acc-auth-card--panel">
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 18 }}>
            <div>
              <div className="acc-auth-panel__eyebrow" style={{ marginBottom: 8 }}>
                {t('acceptInvite.title')}
              </div>
              <div className="acc-auth-card__title" style={{ marginBottom: 8 }}>
                {t('acceptInvite.title')}
              </div>
              <div className="acc-auth-card__description">
                {t('acceptInvite.description')}
              </div>
            </div>
            {detail && tone && (
              <div
                style={{
                  padding: '8px 12px',
                  borderRadius: 999,
                  border: `1px solid ${tone.border}`,
                  background: tone.background,
                  color: tone.color,
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: 'capitalize',
                  boxShadow: 'var(--acc-shadow-xs)',
                }}
              >
                {t(`common:inviteStatuses.${detail.invite.status}`)}
              </div>
            )}
          </div>

          {loading && <div style={{ color: C.muted, fontSize: 14 }}>{t('acceptInvite.loading')}</div>}

          {!loading && pageError && (
            <div
              role="alert"
              style={{
                background: C.dangerBg,
                border: `1px solid ${C.dangerBorder}`,
                borderRadius: 12,
                padding: '14px 16px',
                color: C.dangerText,
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              {pageError}
            </div>
          )}

          {!loading && detail && !pageError && (
            <div style={{ display: 'grid', gap: 18 }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: 12,
                  padding: '16px 18px',
                  borderRadius: 18,
                  border: `1px solid ${C.border}`,
                  background: 'linear-gradient(180deg, #ffffff 0%, var(--acc-color-surface-muted) 100%)',
                }}
              >
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                    {t('acceptInvite.workspace')}
                  </div>
                  <div style={{ fontWeight: 700, color: C.text }}>{detail.organisation.name}</div>
                  <div style={{ fontSize: 13, color: C.muted }}>{detail.organisation.slug}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                    {t('acceptInvite.invitedEmail')}
                  </div>
                  <div style={{ fontWeight: 600, color: C.text }}>{detail.invite.email}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                    {t('acceptInvite.accessRole')}
                  </div>
                  <div style={{ fontWeight: 600, color: C.text }}>{t(`common:roles.${detail.invite.role}`)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                    {t('acceptInvite.expires')}
                  </div>
                  <div style={{ fontWeight: 600, color: C.text }}>{formatDateTime(detail.invite.expiresAt) || t('common:labels.notAvailable')}</div>
                </div>
              </div>

              {detail.invite.message && (
                <div
                  style={{
                    borderRadius: 18,
                    border: `1px solid ${C.border}`,
                    background: 'var(--acc-color-surface)',
                    padding: '14px 16px',
                  }}
                >
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                    {t('acceptInvite.inviteNote')}
                  </div>
                  <div style={{ fontSize: 14, color: C.text, lineHeight: 1.6 }}>{detail.invite.message}</div>
                </div>
              )}

              {detail.invite.status !== 'pending' ? (
                <div
                  style={{
                    borderRadius: 18,
                    border: `1px solid ${tone?.border ?? C.border}`,
                    background: tone?.background ?? '#fff',
                    padding: '14px 16px',
                    color: tone?.color ?? C.text,
                    fontSize: 14,
                    lineHeight: 1.6,
                  }}
                >
                  {detail.invite.status === 'accepted'
                    ? t('acceptInvite.status.accepted')
                    : detail.invite.status === 'revoked'
                      ? t('acceptInvite.status.revoked')
                      : t('acceptInvite.status.expired')}
                </div>
              ) : isAuthenticated ? (
                <div
                  style={{
                    borderRadius: 18,
                    border: `1px solid ${C.border}`,
                    background: 'var(--acc-color-surface)',
                    padding: '18px 18px 16px',
                    display: 'grid',
                    gap: 14,
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 6 }}>
                      {t('acceptInvite.signedInTitle')}
                    </div>
                    <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.6 }}>
                      {emailMatchesInvite
                        ? t('acceptInvite.signedInMatch')
                        : t('acceptInvite.signedInMismatch', {
                            email: user?.email ?? '',
                            inviteEmail: detail.invite.email,
                          })}
                    </div>
                  </div>

                  {inlineFeedback && (
                    <div
                      role="alert"
                      style={{
                        borderRadius: 12,
                        border: `1px solid ${C.dangerBorder}`,
                        background: C.dangerBg,
                        padding: '12px 14px',
                        color: C.dangerText,
                        fontSize: 14,
                        lineHeight: 1.6,
                      }}
                    >
                      {inlineFeedback}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {emailMatchesInvite ? (
                      <Button type="button" onClick={handleAccept} disabled={accepting || authLoading}>
                        {accepting ? t('acceptInvite.acceptSubmitting') : t('acceptInvite.acceptAction')}
                      </Button>
                    ) : (
                      <>
                        <Button type="button" variant="secondary" onClick={() => logout()}>
                          {t('acceptInvite.signOut')}
                        </Button>
                        <Link
                          to={existingUserLoginPath}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 44,
                            padding: '0 18px',
                            borderRadius: 14,
                            background: C.brand,
                            color: '#fff',
                            textDecoration: 'none',
                            fontWeight: 700,
                            boxShadow: '0 16px 28px rgba(15, 39, 71, 0.18)',
                          }}
                        >
                          {t('acceptInvite.continueAction')}
                        </Link>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    borderRadius: 18,
                    border: `1px solid ${C.border}`,
                    background: 'var(--acc-color-surface)',
                    padding: '18px 18px 16px',
                    display: 'grid',
                    gap: 14,
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 6 }}>
                      {t('acceptInvite.existingUserTitle')}
                    </div>
                    <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.6 }}>
                      {requiresExistingAccount
                        ? t('acceptInvite.existingUserRequiresAccount', { email: detail.invite.email })
                        : t('acceptInvite.existingUserOptionalAccount', { email: detail.invite.email })}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <Link
                      to={existingUserLoginPath}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 44,
                        padding: '0 18px',
                        borderRadius: 14,
                        background: C.brand,
                        color: '#fff',
                        textDecoration: 'none',
                        fontWeight: 700,
                        boxShadow: '0 16px 28px rgba(15, 39, 71, 0.18)',
                      }}
                    >
                      {t('acceptInvite.continueAction')}
                    </Link>
                    {!requiresExistingAccount && (
                      <Button type="button" variant="secondary" onClick={() => setShowRegistrationForm(true)}>
                        {t('acceptInvite.createAccountAction')}
                      </Button>
                    )}
                  </div>

                  {!requiresExistingAccount && showRegistrationForm && (
                    <form
                      onSubmit={handleRegister}
                      style={{
                        borderRadius: 18,
                        border: `1px solid ${C.border}`,
                        background: 'var(--card-bg)',
                        padding: '18px 18px 16px',
                        display: 'grid',
                        gap: 14,
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 6 }}>
                          {t('acceptInvite.createAccountTitle')}
                        </div>
                        <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.6 }}>
                          {t('acceptInvite.createAccountDescription', { email: detail.invite.email })}
                        </div>
                      </div>

                      <label style={{ display: 'grid', gap: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {t('acceptInvite.invitedEmailLabel')}
                        </span>
                        <input
                          type="email"
                          value={detail.invite.email}
                          readOnly
                          className="acc-input"
                          style={{ background: 'var(--surface-secondary)', color: C.text }}
                        />
                      </label>

                      <label style={{ display: 'grid', gap: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {t('acceptInvite.fullNameLabel')}
                        </span>
                        <input
                          type="text"
                          value={fullName}
                          onChange={(event) => setFullName(event.target.value)}
                          required
                          className="acc-input"
                        />
                      </label>

                      <label style={{ display: 'grid', gap: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {t('acceptInvite.passwordLabel')}
                        </span>
                        <input
                          type="password"
                          value={password}
                          onChange={(event) => setPassword(event.target.value)}
                          required
                          minLength={8}
                          className="acc-input"
                        />
                      </label>

                      {inlineFeedback && (
                        <div
                          role="alert"
                          style={{
                            borderRadius: 12,
                            border: `1px solid ${C.dangerBorder}`,
                            background: C.dangerBg,
                            padding: '12px 14px',
                            color: C.dangerText,
                            fontSize: 14,
                            lineHeight: 1.6,
                          }}
                        >
                          {inlineFeedback}
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <Button
                          type="submit"
                          disabled={registering || !fullName.trim() || password.length < 8}
                        >
                          {registering ? t('acceptInvite.registerSubmitting') : t('acceptInvite.createAccountAction')}
                        </Button>
                        <Link
                          to="/register"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 44,
                            padding: '0 18px',
                            borderRadius: 14,
                            border: `1px solid ${C.border}`,
                            color: C.text,
                            textDecoration: 'none',
                            fontWeight: 700,
                          }}
                        >
                          {t('acceptInvite.createSeparateWorkspace')}
                        </Link>
                      </div>
                    </form>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
